import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Calendar } from "@shared/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/components/ui/popover";
import { format } from "date-fns";
import {
  CalendarIcon,
  TrendingUp,
  TrendingDown,
  Activity,
  Package,
  AlertTriangle,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import API from "@core/api/api";
import { calculateProductStock, getStockAnalytics } from '@features/products/services/productService';

export function StockAnalyticsDashboard({ productId }) {
  const { t } = useTranslation();
  const [timeRange, setTimeRange] = useState("30d");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Fetch stock analytics
  const { data: analytics, isLoading } = useQuery({
    queryKey: ["stockAnalytics", productId, timeRange, startDate, endDate],
    queryFn: async () => {
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      params.groupBy = timeRange === "7d" ? "day" : "month";

      return await getStockAnalytics(productId, params);
    },
    enabled: !!productId,
  });

  // Fetch stock calculation
  const { data: stockCalculation } = useQuery({
    queryKey: ["stockCalculation", productId],
    queryFn: async () => {
      return await calculateProductStock(productId, { includeDetails: true });
    },
    enabled: !!productId,
  });

  const getStockHealthColor = (health) => {
    const colors = {
      healthy: "bg-chart-2/10 text-chart-2 border-chart-2/20",
      moderate: "bg-chart-4/10 text-chart-4 border-chart-4/20",
      low: "bg-chart-3/10 text-chart-3 border-chart-3/20",
      critical: "bg-destructive/10 text-destructive border-destructive/20",
      out_of_stock: "bg-muted text-muted-foreground border-border",
    };
    return colors[health] || "bg-muted text-muted-foreground border-border";
  };

  const getAdjustmentTypeColor = (type) => {
    const colors = {
      manual: "#3B82F6",
      pickup: "#10B981",
      package: "#EF4444",
      correction: "#F59E0B",
      import: "#8B5CF6",
      export: "#F97316",
      damage: "#DC2626",
      expiry: "#6B7280",
    };
    return colors[type] || "#6B7280";
  };

  const formatChartData = (analytics) => {
    if (!analytics?.analytics) return [];

    return analytics.analytics.map((item) => ({
      date: item._id,
      movements: item.movements,
      totalIncrease: item.totalIncrease,
      totalDecrease: item.totalDecrease,
      netChange: item.netChange,
      averageAdjustment: Math.round(item.averageAdjustment * 100) / 100,
    }));
  };

  const formatPieData = (analytics) => {
    if (!analytics?.analytics) return [];

    const typeCounts = {};
    analytics.analytics.forEach((item) => {
      // This would need to be enhanced to get actual type breakdown
      // For now, we'll use a simplified approach
    });

    return [
      {
        name: t("Increases"),
        value: analytics.summary?.totalIncrease || 0,
        color: "#10B981",
      },
      {
        name: t("Decreases"),
        value: analytics.summary?.totalDecrease || 0,
        color: "#EF4444",
      },
    ];
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {t("Stock Analytics")}
            </div>
            <div className="flex gap-2">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">{t("7 Days")}</SelectItem>
                  <SelectItem value="30d">{t("30 Days")}</SelectItem>
                  <SelectItem value="90d">{t("90 Days")}</SelectItem>
                  <SelectItem value="custom">{t("Custom")}</SelectItem>
                </SelectContent>
              </Select>
              {timeRange === "custom" && (
                <>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-32">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "MMM dd") : t("Start")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-32">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "MMM dd") : t("End")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </>
              )}
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Current Stock")}
                </p>
                <p className="text-2xl font-bold">
                  {stockCalculation?.calculatedStock || 0}
                </p>
              </div>
              <Badge
                className={getStockHealthColor(stockCalculation?.stockHealth)}
              >
                {t(
                  stockCalculation?.stockHealth?.replace("_", " ") || "Unknown"
                )}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Total Movements")}
                </p>
                <p className="text-2xl font-bold">
                  {analytics?.summary?.totalMovements || 0}
                </p>
              </div>
              <Activity className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Total Increase")}
                </p>
                <p className="text-2xl font-bold text-chart-2">
                  +{analytics?.summary?.totalIncrease || 0}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-chart-2" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Total Decrease")}
                </p>
                <p className="text-2xl font-bold text-destructive">
                  -{analytics?.summary?.totalDecrease || 0}
                </p>
              </div>
              <TrendingDown className="h-8 w-8 text-destructive" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock Movement Trend */}
        <Card>
          <CardHeader>
            <CardTitle>{t("Stock Movement Trend")}</CardTitle>
            <CardDescription>
              {t("Stock level changes over time")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={formatChartData(analytics)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="netChange"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  name={t("Net Change")}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Movement Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>{t("Movement Distribution")}</CardTitle>
            <CardDescription>
              {t("Distribution of increases vs decreases")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={formatPieData(analytics)}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {formatPieData(analytics).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Movement Activity */}
      <Card>
        <CardHeader>
          <CardTitle>{t("Movement Activity")}</CardTitle>
          <CardDescription>
            {t("Detailed breakdown of stock movements")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={formatChartData(analytics)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey="totalIncrease"
                fill="#10B981"
                name={t("Increases")}
              />
              <Bar
                dataKey="totalDecrease"
                fill="#EF4444"
                name={t("Decreases")}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Analytics Summary */}
      {analytics && (
        <Card>
          <CardHeader>
            <CardTitle>{t("Analytics Summary")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {t("Average Adjustment")}
                </p>
                <p className="text-2xl font-bold">
                  {analytics.summary?.averageAdjustment?.toFixed(2) || 0}
                </p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {t("Unique Users")}
                </p>
                <p className="text-2xl font-bold">
                  {analytics.summary?.uniqueUsers || 0}
                </p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {t("Validated Movements")}
                </p>
                <p className="text-2xl font-bold">
                  {analytics.summary?.validatedMovements || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts and Warnings */}
      {stockCalculation && stockCalculation?.calculatedStock <= 10 && (
        <Card className="border-chart-4/20 bg-chart-4/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-chart-4">
              <AlertTriangle className="h-5 w-5" />
              {t("Low Stock Alert")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-chart-4">
              {t(
                "Current stock level is low. Consider replenishing inventory."
              )}
            </p>
            <div className="mt-2">
              <Badge
                variant="outline"
                className="text-chart-4 border-chart-4/30"
              >
                {t("Stock Level")}: {stockCalculation?.calculatedStock || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
